import React, { useState } from "react";
import { Target, Eye, EyeOff, RefreshCcw, Mail, Phone, ExternalLink, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ["new", "contacted", "in_progress", "closed"];
const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-600 border-blue-200",
  contacted: "bg-amber-50 text-amber-600 border-amber-200",
  in_progress: "bg-green-50 text-green-600 border-green-200",
  closed: "bg-gray-100 text-gray-500 border-gray-200",
};

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads`, {
        params: { admin_password: password },
      });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Connection error. Make sure the backend is running.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads`, {
        params: { admin_password: password },
      });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError("Failed to refresh leads.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/leads/${leadId}/status`,
        { status: newStatus },
        { params: { admin_password: password } }
      );
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
      );
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-surface-raised">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <div className="w-8 h-8 rounded-xl bg-brand flex items-center justify-center shadow-sm">
              <Target size={15} className="text-white" />
            </div>
            <span className="text-lg font-medium text-ink tracking-tight">
              Apply<span className="text-brand">Strat</span>
              <span className="text-ink-secondary font-normal text-sm ml-1.5">Admin</span>
            </span>
          </div>

          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="bg-white rounded-3xl border border-black/5 shadow-sm p-8"
          >
            <h2 className="text-ink font-medium text-xl mb-1 text-center">Admin Access</h2>
            <p className="text-ink-secondary text-sm text-center mb-7">Enter your password to continue</p>

            <div className="mb-5">
              <label className="block text-sm font-medium text-ink-secondary mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                  className="pr-11 bg-surface-raised border-transparent text-ink placeholder:text-ink-tertiary focus-visible:border-brand focus-visible:ring-brand/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-secondary hover:text-ink transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100">
                <p className="text-red-500 text-xs" data-testid="admin-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !password}
              data-testid="admin-login-button"
              className="w-full rounded-full shadow-sm disabled:scale-100"
            >
              {loading ? "Verifying..." : "Access Dashboard"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-raised" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-6 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-brand flex items-center justify-center">
              <Target size={13} className="text-white" />
            </div>
            <span className="font-medium text-ink tracking-tight">
              Apply<span className="text-brand">Strat</span>
              <span className="text-ink-secondary font-normal text-sm ml-1.5">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-ink-secondary text-sm hidden sm:block">
              <span className="text-ink font-medium">{total}</span> total leads
            </span>
            <button
              onClick={handleRefresh}
              disabled={loading}
              data-testid="admin-refresh-button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 text-ink-secondary hover:text-ink hover:border-brand/40 text-xs font-medium transition-all"
            >
              <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => { setAuthenticated(false); setLeads([]); setPassword(""); setSelectedLead(null); }}
              className="flex items-center gap-1.5 text-ink-secondary hover:text-ink text-xs transition-colors"
            >
              <LogOut size={13} />
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {leads.length === 0 ? (
          <div className="text-center py-32">
            <div className="w-14 h-14 rounded-2xl bg-white border border-black/5 flex items-center justify-center mx-auto mb-5 shadow-sm">
              <Target size={22} className="text-ink-secondary" />
            </div>
            <p className="text-ink font-medium text-base mb-1">No applications yet</p>
            <p className="text-ink-secondary text-sm">New submissions will appear here automatically.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Leads list */}
            <div className="lg:col-span-1 space-y-2.5">
              <p className="text-ink-secondary font-medium text-xs uppercase tracking-widest mb-4">
                Applications ({leads.length})
              </p>
              {leads.map((lead) => (
                <button
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  data-testid={`lead-card-${lead.id}`}
                  className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 ${
                    selectedLead?.id === lead.id
                      ? "bg-white border-brand/40 shadow-sm"
                      : "bg-white border-black/5 hover:border-brand/20 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-ink font-medium text-sm truncate">{lead.full_name}</p>
                      <p className="text-ink-secondary text-xs truncate mt-0.5">{lead.target_roles}</p>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full border text-xs font-medium flex-shrink-0 ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
                      {lead.status}
                    </span>
                  </div>
                  <p className="text-ink-tertiary text-xs mt-2">{new Date(lead.created_at).toLocaleDateString()}</p>
                </button>
              ))}
            </div>

            {/* Lead detail */}
            <div className="lg:col-span-2">
              {selectedLead ? (
                <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-8" data-testid="lead-detail">
                  <div className="flex items-start justify-between gap-4 mb-7">
                    <div>
                      <h2 className="text-ink font-medium text-xl tracking-tight">{selectedLead.full_name}</h2>
                      <p className="text-ink-secondary text-sm mt-1">{selectedLead.target_roles}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-secondary mb-1.5">Status</label>
                      <select
                        value={selectedLead.status}
                        onChange={(e) => handleStatusUpdate(selectedLead.id, e.target.value)}
                        data-testid="lead-status-select"
                        className="px-3 py-2 rounded-xl bg-surface-raised border border-transparent text-ink text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all cursor-pointer"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      { label: "Email", value: selectedLead.email, icon: Mail, href: `mailto:${selectedLead.email}` },
                      { label: "Phone", value: selectedLead.phone, icon: Phone, href: `tel:${selectedLead.phone}` },
                      { label: "Location", value: selectedLead.location },
                      { label: "Work Authorization", value: selectedLead.work_authorization },
                      { label: "Years of Experience", value: selectedLead.years_experience },
                      { label: "Applied", value: new Date(selectedLead.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) },
                    ].map((field) => (
                      <div key={field.label} className="bg-surface-raised rounded-xl p-3.5">
                        <p className="text-ink-secondary text-xs mb-1">{field.label}</p>
                        {field.href ? (
                          <a href={field.href} className="text-brand hover:text-brand-hover text-sm font-medium flex items-center gap-1 transition-colors">
                            {field.value} <ExternalLink size={11} />
                          </a>
                        ) : (
                          <p className="text-ink text-sm font-medium">{field.value || "—"}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {selectedLead.linkedin_url && (
                    <div className="mb-3 bg-surface-raised rounded-xl p-3.5">
                      <p className="text-ink-secondary text-xs mb-1">LinkedIn</p>
                      <a href={selectedLead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-hover text-sm font-medium flex items-center gap-1 transition-colors break-all">
                        {selectedLead.linkedin_url} <ExternalLink size={11} className="flex-shrink-0" />
                      </a>
                    </div>
                  )}

                  {selectedLead.target_companies && (
                    <div className="mb-3 bg-surface-raised rounded-xl p-3.5">
                      <p className="text-ink-secondary text-xs mb-1">Target Companies</p>
                      <p className="text-ink text-sm">{selectedLead.target_companies}</p>
                    </div>
                  )}

                  {selectedLead.notes && (
                    <div className="bg-surface-raised rounded-xl p-3.5">
                      <p className="text-ink-secondary text-xs mb-1">Notes</p>
                      <p className="text-ink text-sm leading-relaxed">{selectedLead.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 bg-white border border-black/5 rounded-3xl shadow-sm">
                  <Target size={24} className="text-[#E5E5EA] mb-3" />
                  <p className="text-ink-secondary text-sm">Select an application to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
